(() => {
	/* ============================================================
	 * CONFIGURATION (ALL URL/VIEW-DEPENDENT VALUES LIVE HERE)
	 * ============================================================ */

	const CONFIG = {
		HOST_SELECTOR: "shopify-forms-embed",
		MAX_WAIT_MS: 15000,
		
		DEBUG: {
			ENABLED: false,
			PREVENT_FORM1_REDIRECT: false,
			LOG_DATALAYER_ON_SUCCESS: false,
			LOG_STEP_CHANGES: false,
			PREVENT_SUBMIT: false,
		},

		/**
		 * View detection for the landing page:
		 *  1) ?step=form1 / ?step=form2a / ?step=form2b (primary)
		 *  2) DOM class fallback (page-form1__form etc.)
		 *
		 * Uses 'step' instead of 'view' to avoid conflict with
		 * Shopify's ?view=landing template selector.
		 */
		VIEW_DETECT: {
			QUERY_PARAM: "step",
			PATH_MATCHERS: [],
		},

		VIEWS: {
			FORM1: "form1",
			FORM2A: "form2a",
			FORM2B: "form2b",
		},

		// Form1 redirects to form2a or form2b on success.
		// Stays on the same landing page URL, switching step via query param.
		REDIRECTS: {
			QUOTE_LABEL: "I need a project quote",
			QUOTE_KEY: "ineedaprojectquote",
			FORM2A_PATH: "step=form2a&email=",
			FORM2B_PATH: "step=form2b&email=",
		},

		STYLE_IDS: {
			GLOBAL: "pw-shadow-css-global",
			FORM1: "pw-shadow-css-form1",
			FORM2A: "pw-shadow-css-form2a",
			FORM2B: "pw-shadow-css-form2b",
		},

		STEP_LABELS: {
			FORM1: "Step 1 of 2",
			FORM2: "Step 2 of 2",
		},

		// Outside page element that should receive data-current-step.
		// Keep this flexible per view. This avoids writing attributes to the wrong node.
		OUTSIDE_STEP_TARGETS: {
			form1: "section.page-form1__form",
			form2a: "section.page-form2a__form, section.page-form1__form",
			form2b: "section.page-form2b__form, section.page-form1__form",
		},

		DROPDOWNS: {
			ROLE_CONTROLS: "rolefunction-selector-dropdown",
			INQUIRY_CONTROLS: "inquiry_type-selector-dropdown",
		},

		FORM2A_REQUIRED: {
			WHERE_LOCATED_CONTROLS:
				"custom#where_are_you_located-selector-dropdown",
			PROJECT_BUDGET_CONTROLS: "custom#project_budget-selector-dropdown",
			PROJECT_FUNDING_STATUS_CONTROLS:
				"custom#project_funding_status_1-selector-dropdown",
			DELIVER_BY_DATE_CONTROLS:
				"custom#deliver_by_date-selector-dropdown",
		},

		PREFILL: {
			EMAIL_QUERY_PARAM: "email",
			LOCK_EMAIL_FIELD: true,
			LOCK_BG: "#d3d3d3",
		},

		FORM2B_REQUIRED: {
			TEXTAREA_NAME: "custom#brief_project_description",
			MIN_LEN: 10,
		},

		VALIDATION: {
			DEBOUNCE_MS: 400,
		},
	};

	/* ============================================================
	 * CSS (4 layers: global + per-view)
	 * ============================================================ */

	const SHADOW_CSS_GLOBAL = `
	/* Kill layout animation without killing loaders/spinners */
	:host *:not(.pw-loader):not(.pw-loader *):not([aria-busy="true"]):not([aria-busy="true"] *):not([class*="_formSubmitButton_"] *):not([class*="_spinner_"]) {
	  transition: none !important;
	  animation: none !important;
	}

	:host {
	  --forms-body-font: normal 400 14px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
	  --forms-heading-font: normal 700 22px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
	}

	section[data-sizing="form-wrapper"] {
	  width: 100% !important;
	  max-width: none !important;
	  position: relative;
	}

	section[data-sizing="form-wrapper"] > div {
	  background: white;
	  border-radius: 10px;
	}

	section[class*="_formHeader_"]{
	  margin-top: 0 !important;
	  margin-bottom: 0 !important;
	}

	section[class*="_formHeader_"] h2,
	section[class*="_formHeader_"] p {
		display: none;
	}

	section[class*="_formHeader_"]::before{
	  content: var(--pw-step-label);
	  z-index: 10;
	  pointer-events: none;
	  text-transform: uppercase;
	  font-weight: 600;
	  font-size: 12px;
	  letter-spacing: 0.08em;
	  color: #2b7bb9;
	  position: absolute;
	  bottom: 30px;
	  right: 30px;
	  background: rgba(0, 0, 0, .05);
	  padding: 7px 12px;
	  border-radius: 5px;
	}

	/* ---------- Validation UI ---------- */
	.is-valid input,
	.is-valid textarea,
	.is-valid button[class*="_selectToggle_"] {
	  border-color: #2ecc71 !important;
	  box-shadow: 0 0 0 1px rgb(65 193 63 / 98%) !important;
	}

	.is-invalid input,
	.is-invalid textarea,
	.is-invalid button[class*="_selectToggle_"] {
	  border-color: #e02229 !important;
	  box-shadow: 0 0 0 1px rgb(241 58 58 / 60%) !important;
	}

	.is-valid input:focus,
	.is-invalid input:focus,
	.is-valid textarea:focus,
	.is-invalid textarea:focus,
	.is-valid button[class*="_selectToggle_"]:focus,
	.is-invalid button[class*="_selectToggle_"]:focus {
	  outline: none !important;
	}

	output[role="alert"] {
	  margin-top: 5px;
	}

	/* Submit button styling (safe default everywhere) */
	form[data-testid="form"] > button[class*="_formSubmitButton_"] {
	  justify-self: start;
	  width: auto;
	  min-width: 0;

	  background: #94caed;
	  color: #193a66;
	  padding: 15px 30px;
	  border-radius: 8px;
	  text-decoration: none;
	  display: block;
	  text-align: center;
	  font-weight: 600;
	  font-size: 16px;
	  letter-spacing: 1px;
	}

	form[data-testid="form"] > button[class*="_formSubmitButton_"][disabled] {
	  background: rgba(0, 0, 0, .1);
	  color: rgba(0, 0, 0, .3);
	}

	#app-embed[data-form-root="true"][data-current-step="success"] section[class*="_formBannerContainer_"] * {
	  text-align: center;
	  justify-content: center;
	}

	@media screen and (max-width: 700px) {
	  section[class*="_formHeader_"]::before {
		position: static;
		display: inline-block;
		margin-bottom: 10px;
	  }
	}
  `;

	const SHADOW_CSS_FORM1 = `
	:host { --pw-step-label: "${CONFIG.STEP_LABELS.FORM1}"; }

	/* 2-col grid layout (form1 only) */
	form[data-testid="form"] {
	  display: grid !important;
	  grid-template-columns: 1fr 1fr;
	  gap: 20px !important;
	}

	form[data-testid="form"] > * { min-width: 0; }

	/* Collapse any Shopify-injected script or empty elements */
	form[data-testid="form"] > script,
	form[data-testid="form"] > div:empty {
	  display: none !important;
	}

	/* First name: left column */
	form[data-testid="form"] > div:has(#first_name) {
	  order: 1;
	  grid-column: 1 / span 1;
	}
	/* Last name: right column */
	form[data-testid="form"] > div:has(#last_name) {
	  order: 2;
	  grid-column: 2 / span 1;
	}

	/* First/Last name: tighten seam */
	form[data-testid="form"] > div:has(#first_name) { margin-right: -10px !important; }
	form[data-testid="form"] > div:has(#last_name)  { margin-left:  -10px !important; }

	form[data-testid="form"] > div:has(#first_name) input {
	  border-top-right-radius: 0 !important;
	  border-bottom-right-radius: 0 !important;
	  border-right: none !important;
	}

	form[data-testid="form"] > div:has(#last_name) input {
	  border-top-left-radius: 0 !important;
	  border-bottom-left-radius: 0 !important;
	}

	/* Email: left column */
	form[data-testid="form"] > div:has(#email){
	  order: 3;
	  grid-column: 1 / span 1;
	}

	/* Phone: right column */
	form[data-testid="form"] > div[class*="_formPhoneInputContainer_"]{
	  order: 4;
	  grid-column: 2 / span 1;
	  gap: 0;
	}

	form[data-testid="form"] > div[class*="_formPhoneInputContainer_"] button[class*="_selectToggle_"] {
	  border-top-right-radius: 0;
	  border-bottom-right-radius: 0;
	}

	form[data-testid="form"] > div[class*="_formPhoneInputContainer_"] input[class*="_formPhoneInputField_"] {
	  border-top-left-radius: 0;
	  border-bottom-left-radius: 0;
	  padding-bottom: calc(var(--form-field-padding-bottom) - 1px);
	}

	/* Newsletter checkbox: full width below */
	form[data-testid="form"] > div[class*="_consentCheckboxContainer_"]{
	  order: 5;
	  grid-column: 1 / -1;
	}

	form[data-testid="form"] > div[class*="_selectContainer_"]{
	  order: 6;
	}
	
	form[data-testid="form"] > button[class*="_formSubmitButton_"] {
		order: 7;
	}

	/* Hide Shopify success UI to prevent flash before redirect (form1 only) */
	#app-embed[data-form-root="true"][data-current-step="success"] [data-testid="banner-success"],
	#app-embed[data-form-root="true"][data-current-step="success"] section[data-testid="banner-success"] {
	  display: none !important;
	}
	#app-embed[data-form-root="true"][data-current-step="success"] {
	  opacity: 0 !important;
	}

	@media screen and (max-width: 700px) {
	  form[data-testid="form"] > div:has(#email),
	  form[data-testid="form"] > div[class*="_formPhoneInputContainer_"],
	  form[data-testid="form"] > div[class*="_selectContainer_"] {
		grid-column: 1 / span 2;
	  }

	  form[data-testid="form"] > div:has(#first_name),
	  form[data-testid="form"] > div:has(#last_name) {
		grid-column: 1 / span 2;
		margin: 0 !important;
	  }

	  form[data-testid="form"] > div:has(#first_name) input,
	  form[data-testid="form"] > div:has(#last_name) input {
		border-radius: 5px;
	  }
	}
  `;

	const SHADOW_CSS_FORM2A = `
	:host { --pw-step-label: "${CONFIG.STEP_LABELS.FORM2}"; }

	form[data-testid="form"] > button[class*="_formSubmitButton_"] {
	  width: auto;
	  min-width: 0;
	  align-self: flex-start;
	  justify-self: flex-start;
	  padding-left: 30px;
	  padding-right: 30px;
	}
  `;

	const SHADOW_CSS_FORM2B = `
	:host { --pw-step-label: "${CONFIG.STEP_LABELS.FORM2}"; }

	form[data-testid="form"] > button[class*="_formSubmitButton_"] {
	  width: auto;
	  min-width: 0;
	  align-self: flex-start;
	  justify-self: flex-start;
	  padding-left: 30px;
	  padding-right: 30px;
	}
  `;

	/* ============================================================
	 * VIEW DETECTION
	 * ============================================================ */

	function getView() {
		const url = new URL(window.location.href);
		const knownViews = [CONFIG.VIEWS.FORM1, CONFIG.VIEWS.FORM2A, CONFIG.VIEWS.FORM2B];

		// 1) Check ?step= param (primary for landing page)
		const qp = String(
			url.searchParams.get(CONFIG.VIEW_DETECT.QUERY_PARAM) || "",
		).trim();
		if (qp && knownViews.includes(qp)) return qp;

		// 2) Check URL path patterns
		const path = String(url.pathname || "");
		for (const m of CONFIG.VIEW_DETECT.PATH_MATCHERS) {
			if (m.re.test(path)) return m.view;
		}

		// 3) Fallback: detect via section class in the DOM
		// (default state on the landing page = form1)
		if (document.querySelector("section.page-form1__form")) return CONFIG.VIEWS.FORM1;
		if (document.querySelector("section.page-form2a__form")) return CONFIG.VIEWS.FORM2A;
		if (document.querySelector("section.page-form2b__form")) return CONFIG.VIEWS.FORM2B;

		return "";
	}

	/* ============================================================
	 * WAIT HELPERS
	 * ============================================================ */

	function now() {
		return typeof performance !== "undefined" && performance.now
			? performance.now()
			: Date.now();
	}

	function normalizeKey(s) {
		return String(s || "")
			.toLowerCase()
			.replace(/\s+/g, "") // remove spaces + newlines
			.trim();
	}
	
	function getFormVariant() {
		// Prefer URL-derived view (most reliable)
		const view = getView();
		if (view === CONFIG.VIEWS.FORM1) return "form1";
		if (view === CONFIG.VIEWS.FORM2A) return "form2a";
		if (view === CONFIG.VIEWS.FORM2B) return "form2b";
	
		// Fallback: detect via outside section class
		const section =
			document.querySelector("section.page-form1__form") ||
			document.querySelector("section.page-form2a__form") ||
			document.querySelector("section.page-form2b__form");
	
		if (!section) return null;
		if (section.classList.contains("page-form1__form")) return "form1";
		if (section.classList.contains("page-form2a__form")) return "form2a";
		if (section.classList.contains("page-form2b__form")) return "form2b";
		return null;
	}

	function waitForHosts(timeoutMs = CONFIG.MAX_WAIT_MS) {
		return new Promise((resolve, reject) => {
			const start = now();
			const existing = document.querySelectorAll(CONFIG.HOST_SELECTOR);
			if (existing.length > 0) return resolve([...existing]);

			const obs = new MutationObserver(() => {
				const hosts = document.querySelectorAll(CONFIG.HOST_SELECTOR);
				if (hosts.length > 0) {
					obs.disconnect();
					resolve([...hosts]);
				} else if (now() - start > timeoutMs) {
					obs.disconnect();
					reject(new Error("Timed out waiting for hosts"));
				}
			});

			obs.observe(document.documentElement, {
				childList: true,
				subtree: true,
			});
			setTimeout(() => {
				obs.disconnect();
				reject(new Error("Timed out waiting for hosts"));
			}, timeoutMs);
		});
	}

	function waitForShadowRoot(host, timeoutMs = CONFIG.MAX_WAIT_MS) {
		return new Promise((resolve, reject) => {
			const start = now();
			if (host.shadowRoot) return resolve(host.shadowRoot);

			let resolved = false;
			const finish = (sr) => {
				if (resolved) return;
				resolved = true;
				clearTimeout(timer);
				if (sr) resolve(sr);
				else reject(new Error("Timed out waiting for shadowRoot"));
			};

			// Trigger 1: customElements.whenDefined fires the moment the Shopify
			// Forms SDK calls customElements.define(), which upgrades all existing
			// elements and attaches their shadow roots synchronously.
			if (typeof customElements !== "undefined" && customElements.whenDefined) {
				customElements
					.whenDefined(host.tagName.toLowerCase())
					.then(() => {
						if (!resolved && host.shadowRoot) finish(host.shadowRoot);
					})
					.catch(function () {});
			}

			// Trigger 2: rAF polling — catches shadow root becoming available at
			// any point (handles deferred/async upgrade paths too).
			const tick = () => {
				if (resolved) return;
				if (host.shadowRoot) return finish(host.shadowRoot);
				if (now() - start > timeoutMs) return finish(null);
				requestAnimationFrame(tick);
			};
			requestAnimationFrame(tick);

			// Hard timeout safety net
			const timer = setTimeout(() => finish(null), timeoutMs);
		});
	}

	/* ============================================================
	 * SHARED HELPERS
	 * ============================================================ */

	function debounce(fn, waitMs) {
		let t = null;
		return (...args) => {
			clearTimeout(t);
			t = setTimeout(() => fn(...args), waitMs);
		};
	}

	function setState(el, state /* 'valid' | 'invalid' | 'clear' */) {
		if (!el) return;
		const container = el.closest("div") || el.parentElement;
		if (!container) return;

		if (state === "valid") {
			container.classList.add("is-valid");
			container.classList.remove("is-invalid");
		} else if (state === "invalid") {
			container.classList.add("is-invalid");
			container.classList.remove("is-valid");
		} else {
			container.classList.remove("is-valid");
			container.classList.remove("is-invalid");
		}
	}

	function getSelectToggleButtonByControls(shadowRoot, controlsContains) {
		return shadowRoot.querySelector(
			`button[aria-controls*="${controlsContains}"]`,
		);
	}

	function getSelectToggleTextSpanByControls(shadowRoot, controlsContains) {
		const btn = getSelectToggleButtonByControls(
			shadowRoot,
			controlsContains,
		);
		if (!btn) return null;
		return btn.querySelector(
			'span[class*="_selectToggleText_"], span[class*="selectToggleText"]',
		);
	}

	function isFilledDropdown(spanEl) {
		return !!spanEl && String(spanEl.textContent || "").trim().length > 0;
	}

	function isFilledInput(el) {
		return !!el && String(el.value || "").trim().length > 0;
	}
	
	/* ============================================================
	 * DATA READERS (for dataLayer payloads)
	 * ============================================================ */
	
	function getRoleText(shadowRoot) {
		const span = getSelectToggleTextSpanByControls(
			shadowRoot,
			CONFIG.DROPDOWNS.ROLE_CONTROLS,
		);
		return String(span?.textContent || "").trim();
	}
	
	function getInquiryText(shadowRoot) {
		const span = getSelectToggleTextSpanByControls(
			shadowRoot,
			CONFIG.DROPDOWNS.INQUIRY_CONTROLS,
		);
		return String(span?.textContent || "").trim();
	}
	
	function getLocationText(shadowRoot) {
		const span = getSelectToggleTextSpanByControls(
			shadowRoot,
			CONFIG.FORM2A_REQUIRED.WHERE_LOCATED_CONTROLS,
		);
		return String(span?.textContent || "").trim();
	}
	
	function getBudgetText(shadowRoot) {
		const span = getSelectToggleTextSpanByControls(
			shadowRoot,
			CONFIG.FORM2A_REQUIRED.PROJECT_BUDGET_CONTROLS,
		);
		return String(span?.textContent || "").trim();
	}
	
	function getFundingStatusText(shadowRoot) {
		const span = getSelectToggleTextSpanByControls(
			shadowRoot,
			CONFIG.FORM2A_REQUIRED.PROJECT_FUNDING_STATUS_CONTROLS,
		);
		return String(span?.textContent || "").trim();
	}
	
	function getDeliveryDateText(shadowRoot) {
		const span = getSelectToggleTextSpanByControls(
			shadowRoot,
			CONFIG.FORM2A_REQUIRED.DELIVER_BY_DATE_CONTROLS,
		);
		return String(span?.textContent || "").trim();
	}
	
	function getBriefDescriptionText(shadowRoot) {
		const textarea = shadowRoot.querySelector(
			`textarea[name="${CSS.escape(CONFIG.FORM2B_REQUIRED.TEXTAREA_NAME)}"]`,
		);
		return String(textarea?.value || "").trim();
	}
	
	function snapshotSubmitValues(shadowRoot, view) {
		const snap = { view, ts: Date.now() };
	
		if (view === CONFIG.VIEWS.FORM1) {
			snap.inquiry_type = getInquiryText(shadowRoot);
			snap.role_function = getRoleText(shadowRoot);
		}
	
		if (view === CONFIG.VIEWS.FORM2A) {
			snap.location = getLocationText(shadowRoot);
			snap.project_budget = getBudgetText(shadowRoot);
			snap.funding_status = getFundingStatusText(shadowRoot);
			snap.delivery_date = getDeliveryDateText(shadowRoot);
		}
	
		if (view === CONFIG.VIEWS.FORM2B) {
			snap.brief_description = getBriefDescriptionText(shadowRoot);
		}
	
		shadowRoot.__pwSubmitSnapshot = snap;
	
		if (CONFIG.DEBUG?.ENABLED) {
			dbg("submit snapshot stored:", snap);
		}
	}
	
	function dbg(...args) {
		if (!CONFIG.DEBUG?.ENABLED) return;
		console.log("[pw-forms]", ...args);
	}
	
	function instrumentDataLayer() {
	  if (!CONFIG.DEBUG?.ENABLED) return;
	  if (window.__pwDataLayerInstrumented) return;
	  window.__pwDataLayerInstrumented = true;
	
	  window.dataLayer = window.dataLayer || [];
	  const originalPush = window.dataLayer.push.bind(window.dataLayer);
	
	  window.dataLayer.push = (...args) => {
		dbg("dataLayer.push called with:", ...args);
		return originalPush(...args);
	  };
	
	  dbg("dataLayer instrumentation enabled");
	}
	
	function logDataLayer(reason) {
		if (!CONFIG.DEBUG?.ENABLED) return;
		if (!CONFIG.DEBUG?.LOG_DATALAYER_ON_SUCCESS) return;
	
		try {
			const dl = window.dataLayer || [];
			dbg(reason, "dataLayer length:", dl.length);
			console.log("[pw-forms] dataLayer:", dl);
		} catch (e) {
			dbg("dataLayer log failed:", e);
		}
	}

	function pushDataLayerOnSuccessOnce(shadowRoot, formVariant, payloadBuilder) {
		if (!formVariant) return;
	
		const firedKey = `__pwDlSuccessFired_${formVariant}`;
		if (shadowRoot[firedKey]) return;
	
		const payload = payloadBuilder?.();
		if (!payload) return;
	
		shadowRoot[firedKey] = true;
	
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push(payload);
	
		if (CONFIG.DEBUG?.ENABLED) {
			dbg("dataLayer PUSHED (success):", payload);
		}
	}

	function pushContactFormSuccess(shadowRoot) {
		const formVariant = getFormVariant();
		if (!formVariant) return;

		pushDataLayerOnSuccessOnce(shadowRoot, formVariant, () => {
			if (formVariant === "form1") {
				const snap = shadowRoot.__pwSubmitSnapshot || {};
				return {
					event: "contact_form_submit",
					step: "1",
					view: "form1",
					form_variant: "1",
					inquiry_type: snap.inquiry_type ?? getInquiryText(shadowRoot),
					role_function: snap.role_function ?? getRoleText(shadowRoot),
				};
			}

			if (formVariant === 'form2a') {
				const snap = shadowRoot.__pwSubmitSnapshot || {};

				return {
					event: 'contact_form_submit',
					step: '2',
					view: 'form2a',
					form_variant: '2a',
					location: snap.location ?? getLocationText(shadowRoot),
					project_budget: snap.project_budget ?? getBudgetText(shadowRoot),
					funding_status: snap.funding_status ?? getFundingStatusText(shadowRoot),
					delivery_date: snap.delivery_date ?? getDeliveryDateText(shadowRoot),
				};
			}

			if (formVariant === "form2b") {
				const snap = shadowRoot.__pwSubmitSnapshot || {};
				const text = snap.brief_description ?? getBriefDescriptionText(shadowRoot);
			
				return {
					event: "contact_form_submit",
					step: "2",
					view: "form2b",
					form_variant: "2b",
					brief_description: text,
				};
			}

			return null;
		});
	}

	/* ============================================================
	 * CSS INJECTION (4 layers)
	 * ============================================================ */

	function ensureStyleEl(shadowRoot, id) {
		let styleEl = shadowRoot.getElementById(id);
		if (!styleEl) {
			styleEl = document.createElement("style");
			styleEl.id = id;
			shadowRoot.appendChild(styleEl);
		}
		return styleEl;
	}

	function setStyleText(styleEl, cssText) {
		if (!styleEl) return;
		if (styleEl.textContent !== cssText) styleEl.textContent = cssText;
	}

	function injectShadowCss(shadowRoot) {
		if (!shadowRoot) return;

		const view = getView();

		const sGlobal = ensureStyleEl(shadowRoot, CONFIG.STYLE_IDS.GLOBAL);
		const sForm1 = ensureStyleEl(shadowRoot, CONFIG.STYLE_IDS.FORM1);
		const sForm2a = ensureStyleEl(shadowRoot, CONFIG.STYLE_IDS.FORM2A);
		const sForm2b = ensureStyleEl(shadowRoot, CONFIG.STYLE_IDS.FORM2B);

		setStyleText(sGlobal, SHADOW_CSS_GLOBAL);
		setStyleText(
			sForm1,
			view === CONFIG.VIEWS.FORM1 ? SHADOW_CSS_FORM1 : "",
		);
		setStyleText(
			sForm2a,
			view === CONFIG.VIEWS.FORM2A ? SHADOW_CSS_FORM2A : "",
		);
		setStyleText(
			sForm2b,
			view === CONFIG.VIEWS.FORM2B ? SHADOW_CSS_FORM2B : "",
		);
	}

	function stylesMissing(shadowRoot) {
		return (
			!shadowRoot.getElementById(CONFIG.STYLE_IDS.GLOBAL) ||
			!shadowRoot.getElementById(CONFIG.STYLE_IDS.FORM1) ||
			!shadowRoot.getElementById(CONFIG.STYLE_IDS.FORM2A) ||
			!shadowRoot.getElementById(CONFIG.STYLE_IDS.FORM2B)
		);
	}

	/* ============================================================
	 * PREFILL (form2a + form2b)
	 * ============================================================ */

	function prefillEmailIfPresent(shadowRoot) {
		const view = getView();
		if (view !== CONFIG.VIEWS.FORM2A && view !== CONFIG.VIEWS.FORM2B)
			return;

		const url = new URL(window.location.href);
		const emailFromUrl = String(
			url.searchParams.get(CONFIG.PREFILL.EMAIL_QUERY_PARAM) || "",
		).trim();
		if (!emailFromUrl) return;

		const emailEl = shadowRoot.querySelector("#email");
		if (!emailEl) return;

		if (String(emailEl.value || "").trim() !== emailFromUrl) {
			emailEl.value = emailFromUrl;
			emailEl.dispatchEvent(new Event("input", { bubbles: true }));
		}

		if (CONFIG.PREFILL.LOCK_EMAIL_FIELD) {
			emailEl.readOnly = true;
			emailEl.style.backgroundColor = CONFIG.PREFILL.LOCK_BG;
		}

		const emailContainer = emailEl.closest("div");
		if (emailContainer) emailContainer.style.display = "none";

		// Late swap guard (Shopify sometimes replaces nodes)
		if (shadowRoot.__pwEmailPrefillArmed) return;
		shadowRoot.__pwEmailPrefillArmed = true;

		let tries = 0;
		const maxTries = 40;
		const t = setInterval(() => {
			tries += 1;

			const el = shadowRoot.querySelector("#email");
			if (el) {
				if (String(el.value || "").trim() !== emailFromUrl) {
					el.value = emailFromUrl;
					el.dispatchEvent(new Event("input", { bubbles: true }));
				}
				if (CONFIG.PREFILL.LOCK_EMAIL_FIELD) {
					el.readOnly = true;
					el.style.backgroundColor = CONFIG.PREFILL.LOCK_BG;
				}
				const c = el.closest("div");
				if (c) c.style.display = "none";
			}

			if (tries >= maxTries) clearInterval(t);
		}, 100);
	}

	/* ============================================================
	 * MIRROR data-current-step OUTSIDE SHADOWROOT
	 * ============================================================ */

	function getOutsideStepTarget(view) {
		const sel =
			CONFIG.OUTSIDE_STEP_TARGETS[view] ||
			CONFIG.OUTSIDE_STEP_TARGETS.form1;
		return document.querySelector(sel);
	}

	function mirrorCurrentStepToHost(shadowRoot) {
		const stepEl = shadowRoot.querySelector(
			'div[data-form-root="true"][data-current-step]',
		);
		if (!stepEl) return;

		const view = getView();
		const target = getOutsideStepTarget(view);
		if (!target) return;

		const sync = () => {
			const step = stepEl.getAttribute("data-current-step") || "";
			if (!step) return;

			target.setAttribute("data-current-step", step);

			if (step === "success") {
				pushContactFormSuccess(shadowRoot);
			}
		};

		// If Shopify swaps the step node later, the style-integrity observer will call this again.
		if (shadowRoot.__pwStepEl !== stepEl) {
			try {
				shadowRoot.__pwStepObserver?.disconnect();
			} catch (e) {}

			shadowRoot.__pwStepEl = stepEl;

			shadowRoot.__pwStepObserver = new MutationObserver(sync);
			shadowRoot.__pwStepObserver.observe(stepEl, {
				attributes: true,
				attributeFilter: ["data-current-step"],
			});
		}

		sync();
	}
	
	function watchForSuccessAndDebug(shadowRoot, onSuccess) {
		const appRoot =
			shadowRoot.querySelector('#app-embed[data-form-root="true"]') ||
			shadowRoot.querySelector('[data-form-root="true"]');
		if (!appRoot) return;
	
		// One-shot guard per page load
		if (shadowRoot.__pwSuccessDebugBound) return;
		shadowRoot.__pwSuccessDebugBound = true;
	
		const handle = () => {
			const step = appRoot.getAttribute("data-current-step") || "";
			if (CONFIG.DEBUG?.ENABLED && CONFIG.DEBUG?.LOG_STEP_CHANGES) {
				dbg("step:", step);
			}
	
			if (step !== "success") return;
	
			// Fire once
			if (shadowRoot.__pwSuccessDebugFired) return;
			shadowRoot.__pwSuccessDebugFired = true;
	
			try {
				onSuccess?.();
			} finally {
				logDataLayer("after success");
			}
		};
	
		// Run once now (covers cases where success is already set)
		handle();
	
		const obs = new MutationObserver(handle);
		obs.observe(appRoot, { attributes: true, attributeFilter: ["data-current-step"] });
	}

	/* ============================================================
	 * MAIN BEHAVIOR
	 * ============================================================ */

	function applyFormCustomizations(shadowRoot) {
		// Keep this call cheap: only inject when missing or view changed.
		const view = getView();
		if (stylesMissing(shadowRoot) || shadowRoot.__pwCssView !== view) {
			shadowRoot.__pwCssView = view;
			injectShadowCss(shadowRoot);
		}

		prefillEmailIfPresent(shadowRoot);
		mirrorCurrentStepToHost(shadowRoot);

		const submit = shadowRoot.querySelector('button[type="submit"]');
		const form = shadowRoot.querySelector('form[data-testid="form"]');
		if (!submit || !form) return;
		
		// Capture field values BEFORE Shopify mutates/re-renders on success
		if (form.dataset.pwSubmitSnapshotBound !== "1") {
			form.dataset.pwSubmitSnapshotBound = "1";
		
			form.addEventListener(
				"submit",
				() => {
					const view = getView();
					snapshotSubmitValues(shadowRoot, view);
				},
				true, // capture phase so it runs early
			);
		}
		
		// DEBUG: prevent Shopify Forms from actually submitting (keeps UI in-place for inspection)
		if (CONFIG.DEBUG?.ENABLED && CONFIG.DEBUG?.PREVENT_SUBMIT) {
			if (form.dataset.pwDebugPreventSubmit !== "1") {
				form.dataset.pwDebugPreventSubmit = "1";
		
				form.addEventListener(
					"submit",
					(e) => {
						e.preventDefault();
						e.stopImmediatePropagation();
		
						dbg("DEBUG: form submit prevented for view:", getView());
						dbg("DEBUG: submit button disabled?", submit.disabled);
		
						// Snapshot current dataLayer for quick inspection
						logDataLayer("after prevented submit");
					},
					true, // capture
				);
			}
		}
		
		watchForSuccessAndDebug(shadowRoot, () => {
			dbg("SUCCESS detected for view:", getView());
		
			// Push the one-time success event here (single-fire guarded per variant)
			pushContactFormSuccess(shadowRoot);
		
			// Always log after pushing (or attempting to)
			logDataLayer("after success");
		});

		/* =========================
		 * FORM2A
		 * ========================= */
		if (view === CONFIG.VIEWS.FORM2A) {
			if (form.dataset.pwBoundForm2a === "1") return;
			form.dataset.pwBoundForm2a = "1";

			const FIELDS = [
				{
					key: "where",
					controls: CONFIG.FORM2A_REQUIRED.WHERE_LOCATED_CONTROLS,
				},
				{
					key: "budget",
					controls: CONFIG.FORM2A_REQUIRED.PROJECT_BUDGET_CONTROLS,
				},
				{
					key: "funding",
					controls:
						CONFIG.FORM2A_REQUIRED.PROJECT_FUNDING_STATUS_CONTROLS,
				},
				{
					key: "date",
					controls: CONFIG.FORM2A_REQUIRED.DELIVER_BY_DATE_CONTROLS,
				},
			];

			const state = {
				forceShowErrors: false,
				fields: Object.fromEntries(
					FIELDS.map((f) => [
						f.key,
						{ touched: false, idle: false, __idleT: null },
					]),
				),
			};

			const shouldShow = (key) => {
				const s = state.fields[key];
				return state.forceShowErrors || s.touched || s.idle;
			};

			const validateField = (f) => {
				const btn = getSelectToggleButtonByControls(
					shadowRoot,
					f.controls,
				);
				const span = getSelectToggleTextSpanByControls(
					shadowRoot,
					f.controls,
				);
				const ok = isFilledDropdown(span);

				if (btn && shouldShow(f.key))
					setState(btn, ok ? "valid" : "invalid");
				else if (btn) setState(btn, "clear");

				return ok;
			};

			const validateAll = () => {
				const ok = FIELDS.every((f) => validateField(f));
				submit.disabled = !ok;
			};

			const touchAndIdle = (key) => {
				const s = state.fields[key];
				s.touched = true;
				s.idle = false;

				clearTimeout(s.__idleT);
				s.__idleT = setTimeout(() => {
					s.idle = true;
					validateAll();
				}, CONFIG.VALIDATION.DEBOUNCE_MS);
			};

			const spanObserver = new MutationObserver(validateAll);

			for (const f of FIELDS) {
				const span = getSelectToggleTextSpanByControls(
					shadowRoot,
					f.controls,
				);
				if (!span) continue;
				spanObserver.observe(span, {
					childList: true,
					subtree: true,
					characterData: true,
				});
			}

			shadowRoot.addEventListener("click", (e) => {
				const clickedBtn = e.target.closest("button");

				for (const f of FIELDS) {
					const btn = getSelectToggleButtonByControls(
						shadowRoot,
						f.controls,
					);
					if (btn && clickedBtn === btn) touchAndIdle(f.key);
					if (e.target.closest(`[id*="${f.controls}"]`))
						touchAndIdle(f.key);
				}

				setTimeout(validateAll, 0);
			});

			submit.addEventListener("click", () => {
				if (!submit.disabled) return;

				state.forceShowErrors = true;
				for (const f of FIELDS) {
					const s = state.fields[f.key];
					s.touched = true;
					s.idle = true;
				}
				validateAll();
			});

			validateAll();
			return;
		}

		/* =========================
		 * FORM2B
		 * ========================= */
		if (view === CONFIG.VIEWS.FORM2B) {
			if (form.dataset.pwBoundForm2b === "1") return;
			form.dataset.pwBoundForm2b = "1";

			const desc = shadowRoot.querySelector(
				`textarea[name="${CSS.escape(CONFIG.FORM2B_REQUIRED.TEXTAREA_NAME)}"]`,
			);
			if (!desc) return;

			const state = {
				blurred: false,
				idleFired: false,
				forceShowErrors: false,
			};

			const countTrimmed = () => String(desc.value || "").trim().length;
			const isValid = () =>
				countTrimmed() >= CONFIG.FORM2B_REQUIRED.MIN_LEN;

			const shouldShow = () =>
				state.forceShowErrors || state.blurred || state.idleFired;

			const validate = () => {
				const ok = isValid();
				if (shouldShow()) setState(desc, ok ? "valid" : "invalid");
				else setState(desc, "clear");
				submit.disabled = !ok;
				return ok;
			};

			const debouncedValidate = debounce(() => {
				state.idleFired = true;
				validate();
			}, CONFIG.VALIDATION.DEBOUNCE_MS);

			desc.addEventListener("input", () => {
				state.idleFired = false;
				debouncedValidate();
				validate(); // keeps gating live without painting errors immediately
			});

			desc.addEventListener("blur", () => {
				state.blurred = true;
				state.idleFired = true;
				validate();
			});

			submit.addEventListener("click", () => {
				if (!submit.disabled) return;
				state.forceShowErrors = true;
				state.blurred = true;
				state.idleFired = true;
				validate();
			});

			// const getFormVariant = () => {
			// 	// outer DOM decides which form we’re on
			// 	const section =
			// 		document.querySelector('section.page-form1__form') ||
			// 		document.querySelector('section.page-form2a__form') ||
			// 		document.querySelector('section.page-form2b__form');
// 
			// 	if (!section) return null;
// 
			// 	if (section.classList.contains('page-form1__form')) return 'form1';
			// 	if (section.classList.contains('page-form2a__form')) return 'form2a';
			// 	if (section.classList.contains('page-form2b__form')) return 'form2b';
// 
			// 	return null;
			// };

			validate();
			return;
		}

		/* =========================
		 * FORM1
		 * ========================= */
		if (view !== CONFIG.VIEWS.FORM1) return;
		if (form.dataset.pwBoundForm1 === "1") return;
		form.dataset.pwBoundForm1 = "1";

		const email = shadowRoot.querySelector("#email");
		const phone = shadowRoot.querySelector("#phone_number");
		const firstName = shadowRoot.querySelector("#first_name");
		const lastName = shadowRoot.querySelector("#last_name");

		const roleControls = CONFIG.DROPDOWNS.ROLE_CONTROLS;
		const inquiryControls = CONFIG.DROPDOWNS.INQUIRY_CONTROLS;

		const touched = {
			firstName: false,
			lastName: false,
			email: false,
			phone: false,
			role: false,
			inquiry: false,
		};

		let forceShowErrors = false;

		const validateEmail = () => {
			if (!email) return true;
			const ok = /.+@.+\..+/.test(String(email.value || "").trim());
			if (touched.email || forceShowErrors)
				setState(email, ok ? "valid" : "invalid");
			else setState(email, "clear");
			return ok;
		};

		const validatePhone = () => {
			if (!phone) return true;
			const digits = String(phone.value || "").replace(/\D/g, "");
			const ok = digits.length >= 10 && digits.length <= 15;
			if (touched.phone || forceShowErrors)
				setState(phone, ok ? "valid" : "invalid");
			else setState(phone, "clear");
			return ok;
		};

		const validateFirstName = () => {
			if (!firstName) return true;
			const ok = isFilledInput(firstName);
			if (touched.firstName || forceShowErrors)
				setState(firstName, ok ? "valid" : "invalid");
			else setState(firstName, "clear");
			return ok;
		};

		const validateLastName = () => {
			if (!lastName) return true;
			const ok = isFilledInput(lastName);
			if (touched.lastName || forceShowErrors)
				setState(lastName, ok ? "valid" : "invalid");
			else setState(lastName, "clear");
			return ok;
		};

		const validateRole = () => {
			const btn = getSelectToggleButtonByControls(
				shadowRoot,
				roleControls,
			);
			const span = getSelectToggleTextSpanByControls(
				shadowRoot,
				roleControls,
			);
			const ok = isFilledDropdown(span);

			if (btn && (touched.role || forceShowErrors))
				setState(btn, ok ? "valid" : "invalid");
			else if (btn) setState(btn, "clear");

			return ok;
		};

		const validateInquiry = () => {
			const btn = getSelectToggleButtonByControls(
				shadowRoot,
				inquiryControls,
			);
			const span = getSelectToggleTextSpanByControls(
				shadowRoot,
				inquiryControls,
			);
			const ok = isFilledDropdown(span);

			if (btn && (touched.inquiry || forceShowErrors))
				setState(btn, ok ? "valid" : "invalid");
			else if (btn) setState(btn, "clear");

			return ok;
		};

		const validateAll = () => {
			const fnOk = validateFirstName();
			const lnOk = validateLastName();
			const emOk = validateEmail();
			const phOk = validatePhone();
			const roleOk = validateRole();
			const inqOk = validateInquiry();

			submit.disabled = !(
				fnOk &&
				lnOk &&
				roleOk &&
				inqOk &&
				emOk &&
				phOk
			);
		};

		const bindInputTouched = (el, key) => {
			if (!el) return;
			const handler = () => {
				touched[key] = true;
				validateAll();
			};
			el.addEventListener("input", handler);
			el.addEventListener("blur", handler);
		};

		bindInputTouched(firstName, "firstName");
		bindInputTouched(lastName, "lastName");
		bindInputTouched(email, "email");
		bindInputTouched(phone, "phone");

		const roleSpan = getSelectToggleTextSpanByControls(
			shadowRoot,
			roleControls,
		);
		const inquirySpan = getSelectToggleTextSpanByControls(
			shadowRoot,
			inquiryControls,
		);

		const dropdownObserver = new MutationObserver(validateAll);
		if (roleSpan)
			dropdownObserver.observe(roleSpan, {
				childList: true,
				subtree: true,
				characterData: true,
			});
		if (inquirySpan)
			dropdownObserver.observe(inquirySpan, {
				childList: true,
				subtree: true,
				characterData: true,
			});

		shadowRoot.addEventListener("click", (e) => {
			const roleBtn = getSelectToggleButtonByControls(
				shadowRoot,
				roleControls,
			);
			const inquiryBtn = getSelectToggleButtonByControls(
				shadowRoot,
				inquiryControls,
			);

			if (roleBtn && e.target.closest("button") === roleBtn)
				touched.role = true;
			if (inquiryBtn && e.target.closest("button") === inquiryBtn)
				touched.inquiry = true;

			if (e.target.closest(`[id*="${roleControls}"]`))
				touched.role = true;
			if (e.target.closest(`[id*="${inquiryControls}"]`))
				touched.inquiry = true;

			setTimeout(validateAll, 0);
		});

		submit.addEventListener("click", () => {
			if (!submit.disabled) return;

			forceShowErrors = true;
			touched.firstName = true;
			touched.lastName = true;
			touched.email = true;
			touched.phone = true;
			touched.role = true;
			touched.inquiry = true;

			validateAll();
		});

		validateAll();

		/* ---- Redirect after Shopify success (form1 only) ---- */

		const getInquiryKey = () => {
			const span = getSelectToggleTextSpanByControls(
				shadowRoot,
				inquiryControls,
			);
			return normalizeKey(span?.textContent);
		};

		const getEmailValue = () => String(email?.value || "").trim();

		// Transition to a step without a full page reload.
	// Updates the URL via pushState, toggles the CSS attribute so the right
	// form section becomes visible, then re-applies CSS + validation to all
	// hosts so they reflect the new view (getView() now returns the new step).
	const activateStep = (url) => {
		const stepMatch = url.match(/[?&]step=(form2a|form2b)/);
		const step = stepMatch ? stepMatch[1] : null;
		if (!step) {
			window.location.assign(url);
			return;
		}
		history.pushState({ lpStep: step }, "", url);
		document.documentElement.setAttribute("data-lp-step", step);

		// Force every host to re-evaluate CSS and validation against the new view.
		document.querySelectorAll("shopify-forms-embed").forEach((host) => {
			const sr = host.shadowRoot;
			if (!sr) return;
			sr.__pwCssView = null; // invalidate cached view so injectShadowCss re-runs
			injectShadowCss(sr);
			applyFormCustomizations(sr);
		});

		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const buildRedirectUrl = () => {
			const inquiryKey = pwInquiryAtSubmit || getInquiryKey();
			const emailVal = pwEmailAtSubmit || getEmailValue();

			const stepParam =
				inquiryKey === CONFIG.REDIRECTS.QUOTE_KEY
					? CONFIG.REDIRECTS.FORM2A_PATH
					: CONFIG.REDIRECTS.FORM2B_PATH;

			// Stay on the current page, preserve Shopify's ?view= param
			const url = new URL(window.location.href);
			const viewParam = url.searchParams.get("view") || "";
			const basePath = url.pathname;
			const qs = viewParam
				? `?view=${encodeURIComponent(viewParam)}&${stepParam}${encodeURIComponent(emailVal)}`
				: `?${stepParam}${encodeURIComponent(emailVal)}`;

			return basePath + qs;
		};

		let pwRedirecting = false;
		let pwSuccessObserver = null;

		const startWatchingForSuccess = () => {
			try {
				pwSuccessObserver?.disconnect();
			} catch (e) {}
			pwSuccessObserver = null;

			const appRoot =
				shadowRoot.querySelector('#app-embed[data-form-root="true"]') ||
				shadowRoot.querySelector('[data-form-root="true"]');
			if (!appRoot) return;

			if (appRoot.getAttribute("data-current-step") === "success") {
				pwRedirecting = true;
				queueMicrotask(() => {
					const url = buildRedirectUrl();
				
					if (CONFIG.DEBUG?.ENABLED && CONFIG.DEBUG?.PREVENT_FORM1_REDIRECT) {
						dbg("DEBUG: redirect prevented. Would have redirected to:", url);
						return;
					}
				
					activateStep(url);
				});
				return;
			}

			pwSuccessObserver = new MutationObserver(() => {
				if (pwRedirecting) return;
				const step = appRoot.getAttribute("data-current-step");
				if (step === "success") {
					pwRedirecting = true;
					queueMicrotask(() => {
						const url = buildRedirectUrl();
					
						if (CONFIG.DEBUG?.ENABLED && CONFIG.DEBUG?.PREVENT_FORM1_REDIRECT) {
							dbg("DEBUG: redirect prevented. Would have redirected to:", url);
							return;
						}
					
						activateStep(url);
					});
				}
			});

			pwSuccessObserver.observe(appRoot, {
				attributes: true,
				attributeFilter: ["data-current-step"],
			});
		};

		submit.addEventListener("click", () => {
			if (submit.disabled) return;

			pwInquiryAtSubmit = getInquiryKey();
			pwEmailAtSubmit = getEmailValue();

			pwRedirecting = false;
			startWatchingForSuccess();
		});
	}

	/* ============================================================
	 * RERENDER RESILIENCE
	 * ============================================================ */

	function observeStyleIntegrity(shadowRoot) {
		const obs = new MutationObserver(() => {
			if (stylesMissing(shadowRoot)) {
				injectShadowCss(shadowRoot);
				return;
			}

			const nextView = getView();
			if (shadowRoot.__pwCssView !== nextView) {
				shadowRoot.__pwCssView = nextView;
				injectShadowCss(shadowRoot);
				applyFormCustomizations(shadowRoot);
			} else {
				// Shopify likes to swap nodes without changing view; keep step mirroring alive.
				mirrorCurrentStepToHost(shadowRoot);
			}
		});

		obs.observe(shadowRoot, { childList: true, subtree: true });
		return obs;
	}

	/* ============================================================
	 * INIT (multi-instance: initializes ALL form hosts on the page)
	 * ============================================================ */

	async function initHost(host) {
		let shadowRoot;
		try {
			shadowRoot = await waitForShadowRoot(host);

			// Inject CSS first (prevents FOUC — form is still visibility:hidden)
			injectShadowCss(shadowRoot);

			// Attach behavior
			applyFormCustomizations(shadowRoot);

			// No need to wait for stable paint: visibility:hidden already prevents
			// FOUC, so we can mark ready immediately after CSS + behavior are set.
		} catch (err) {
			console.warn("[forms-shadow] initHost setup failed:", err);
		} finally {
			// Always mark ready so the embed becomes visible (hides pw-loader)
			host.setAttribute("data-pw-ready", "1");
		}

		// After ready: only re-add styles if Shopify removes them, or view flips.
		if (shadowRoot) observeStyleIntegrity(shadowRoot);
	}

	async function init() {
		try {
			const hosts = await waitForHosts();

			instrumentDataLayer();

			// Init each form host independently
			await Promise.all(hosts.map((host) => initHost(host)));
		} catch (err) {
			console.warn("[forms-shadow] init failed:", err);
		}
	}

	// Document-level safety net: if the promise chain stalls for any reason,
	// inject CSS + mark all embeds visible after 3 seconds so the form never
	// loads forever and always shows with correct styling.
	setTimeout(function () {
		document.querySelectorAll(CONFIG.HOST_SELECTOR).forEach(function (el) {
			if (el.shadowRoot) {
				injectShadowCss(el.shadowRoot);
				applyFormCustomizations(el.shadowRoot);
			}
			el.setAttribute("data-pw-ready", "1");
		});
	}, 3000);

	init();
})();
